import { useEffect, useState } from "react"

import { BuildingReferenceSearchResult } from "../types/GlobalSearch"
import { computeVariantKeyMatchInfo } from "./computeVariantKeyMatchInfo"
import { fetchBuildingReferenceVariantKeys } from "./fetchBuildingReferenceVariantKeys"
import { fetchBuildingReferencesByVariantKey } from "./fetchBuildingReferencesByVariantKey"

interface UseBuildingReferencesByVariantKeyProps {
  variantKeys?: string[] | null
}

export function useBuildingReferencesByVariantKey({
  variantKeys
}: UseBuildingReferencesByVariantKeyProps): {
  results: BuildingReferenceSearchResult[]
  isLoading: boolean
} {
  const [results, setResults] = useState<BuildingReferenceSearchResult[]>([])
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!variantKeys || variantKeys.length === 0) {
      setResults([])
      setIsLoading(false)
      return undefined
    }

    let isCancelled = false
    setIsLoading(true)

    fetchBuildingReferencesByVariantKey(variantKeys)
      .then(objs => {
        if (!isCancelled) {
          Promise.all(
            objs.map(async obj => {
              const vKeys = await fetchBuildingReferenceVariantKeys(obj)
              return { obj, variantKeys: vKeys }
            })
          )
            .then(buildingReferencesWithVariantKeys => {
              if (!isCancelled) {
                setResults(
                  buildingReferencesWithVariantKeys.map(
                    ({ obj, variantKeys: vKeys }) => ({
                      obj,
                      id: obj.id(),
                      title: obj.get("title"),
                      matchInfo: {
                        products: computeVariantKeyMatchInfo(variantKeys, vKeys)
                      }
                    })
                  )
                )
              }
            })
            .catch(() => {
              if (!isCancelled) setResults([])
            })
        }
      })
      .catch(() => {
        if (!isCancelled) setResults([])
      })
      .finally(() => {
        if (!isCancelled) setIsLoading(false)
      })

    return () => {
      isCancelled = true
    }
  }, [variantKeys])

  return { results, isLoading }
}
